import React, { useMemo, useState } from "react";

function normalize(s) {
	return (s || "").trim().toLowerCase();
}

function templateInfo(t) {
	const id = t.Id ?? t.id;
	const nom = t.Nom ?? t.nom ?? "Sans nom";
	const exos = t.ListExercice ?? t.listExercice ?? t.exercices ?? [];
	const seriesTotal = exos.reduce((sum, ex) => {
		if (typeof ex === "string") return sum;
		const s = ex.ListSerie ?? ex.listSerie ?? ex.series ?? [];
		return sum + (Array.isArray(s) ? s.length : 0);
	}, 0);
	return { id, nom, exos, seriesTotal };
}

function exoNames(exos, max = 3) {
	return exos.slice(0, max).map((ex) =>
		typeof ex === "string" ? ex : (ex.NomExercice ?? ex.nomExercice ?? ex.nom ?? "")
	);
}

export default function TemplatesPanel({
	studentTemplates,
	coachTemplates,
	templateFolders = [],
	templateFolderAssoc = [],
	studentName,
	loading,
	onOpenTemplate,
	onImportTemplate,
	libraryMode = false,
}) {
	const [mode, setMode] = useState("list"); // "list" | "import"
	const [importQuery, setImportQuery] = useState("");
	const [importingId, setImportingId] = useState(null);
	const [activeFolderKey, setActiveFolderKey] = useState(null);

	const folders = useMemo(
		() => (templateFolders || []).filter((f) => !(f.Supprime ?? f.supprime)),
		[templateFolders]
	);

	const folderCount = (folderKey) =>
		(templateFolderAssoc || []).filter((a) => {
			const aSupprime = a.Supprime ?? a.supprime;
			const aFolderKey = a.FolderKey ?? a.folderKey;
			return !aSupprime && aFolderKey === folderKey;
		}).length;

	const importList = useMemo(() => {
		const q = normalize(importQuery);
		const studentNamesSet = new Set((studentTemplates || []).map((t) => normalize(t.nom ?? t.Nom)));
		return (coachTemplates || [])
			.filter((t) => {
				const nom = t.Nom ?? t.nom ?? "";
				if (q && !normalize(nom).includes(q)) return false;
				if (activeFolderKey !== null) {
					const tKey = t.Key ?? t.key;
					const inFolder = (templateFolderAssoc || []).some((a) => {
						const aSupprime = a.Supprime ?? a.supprime;
						const aFolderKey = a.FolderKey ?? a.folderKey;
						const aTemplateKey = a.TemplateKey ?? a.templateKey;
						return !aSupprime && aFolderKey === activeFolderKey && aTemplateKey === tKey;
					});
					if (!inFolder) return false;
				}
				return true;
			})
			.map((t) => ({ ...t, _inStudent: studentNamesSet.has(normalize(t.Nom ?? t.nom)) }));
	}, [coachTemplates, importQuery, studentTemplates, activeFolderKey, templateFolderAssoc]);

	const handleImport = async (t) => {
		if (!onImportTemplate) return;
		const id = t.Id ?? t.id;
		setImportingId(id);
		await onImportTemplate(t);
		setImportingId(null);
	};

	// ===== Import sub-view =====
	if (mode === "import") {
		return (
			<div className="editor-panel-templates">
				<button className="editor-panel-exercises-back" onClick={() => { setMode("list"); setImportQuery(""); }}>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
						<line x1="19" y1="12" x2="5" y2="12" />
						<polyline points="12 19 5 12 12 5" />
					</svg>
					Mes templates
				</button>

				<div className="editor-panel-exercises-subtitle">
					Importer dans la bibliothèque de {studentName || "l'élève"}
				</div>

				<div className="editor-panel-exercises-search">
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<circle cx="11" cy="11" r="8" />
						<line x1="21" y1="21" x2="16.65" y2="16.65" />
					</svg>
					<input
						type="text"
						value={importQuery}
						onChange={(e) => setImportQuery(e.target.value)}
						placeholder="Rechercher dans mes templates"
						autoFocus
					/>
					{importQuery && (
						<button className="editor-panel-exercises-clear" onClick={() => setImportQuery("")}>×</button>
					)}
				</div>

				{folders.length > 0 && (
					<div className="editor-panel-templates-folderpills">
						<button
							className={`editor-panel-templates-folderpill ${activeFolderKey === null ? "active" : ""}`}
							onClick={() => setActiveFolderKey(null)}
						>
							Tous <span className="cnt">{(coachTemplates || []).length}</span>
						</button>
						{folders.map((f) => {
							const fKey = f.Key ?? f.key;
							const fName = f.NomDossier ?? f.nomDossier ?? "Dossier";
							return (
								<button
									key={fKey}
									className={`editor-panel-templates-folderpill ${activeFolderKey === fKey ? "active" : ""}`}
									onClick={() => setActiveFolderKey(fKey)}
								>
									{fName} <span className="cnt">{folderCount(fKey)}</span>
								</button>
							);
						})}
					</div>
				)}

				<div className="editor-panel-templates-list">
					{importList.length === 0 ? (
						<div className="editor-panel-templates-empty">Aucun template dans tes modèles</div>
					) : (
						importList.map((t) => {
							const { id, nom, exos, seriesTotal } = templateInfo(t);
							return (
								<button
									key={id}
									className={`editor-panel-templates-card ${t._inStudent ? "is-disabled" : ""}`}
									onClick={() => !t._inStudent && handleImport(t)}
									disabled={t._inStudent || importingId === id}
								>
									<div className="editor-panel-templates-card-name">{nom}</div>
									<div className="editor-panel-templates-card-stats">
										{exos.length} exo{exos.length > 1 ? "s" : ""}
										{seriesTotal > 0 && ` · ${seriesTotal} sér.`}
									</div>
									<div className="editor-panel-templates-card-cta">
										{t._inStudent ? "Déjà dans sa biblio" : importingId === id ? "Import…" : "Importer →"}
									</div>
								</button>
							);
						})
					)}
				</div>
			</div>
		);
	}

	// ===== Main list view =====
	const mainList = libraryMode ? coachTemplates : studentTemplates;
	return (
		<div className="editor-panel-templates">
			<div className="editor-panel-exercises-owner">
				{libraryMode ? <strong>Mes templates</strong> : <>Templates de <strong>{studentName || "l'élève"}</strong></>}
			</div>

			{!libraryMode && (
				<div className="editor-panel-templates-importbar top">
					<button className="editor-panel-exercises-new-btn" onClick={() => setMode("import")}>
						+ Importer un template
					</button>
				</div>
			)}

			<div className="editor-panel-templates-list">
				{loading ? (
					<div className="editor-panel-templates-empty">Chargement…</div>
				) : !mainList || mainList.length === 0 ? (
					<div className="editor-panel-templates-empty">
						{libraryMode ? "Aucun template dans ta bibliothèque" : "Aucun template pour cet élève"}
					</div>
				) : (
					mainList.map((t) => {
						const { id, nom, exos, seriesTotal } = templateInfo(t);
						const names = exoNames(exos);
						return (
							<button
								key={id}
								className="editor-panel-templates-card"
								onClick={() => onOpenTemplate?.(t)}
								title={libraryMode ? "Partir de ce template" : "Charger ce template comme nouvelle séance"}
							>
								<div className="editor-panel-templates-card-name">{nom}</div>
								<div className="editor-panel-templates-card-stats">
									{exos.length} exo{exos.length > 1 ? "s" : ""}
									{seriesTotal > 0 && ` · ${seriesTotal} sér.`}
								</div>
								{names.length > 0 && (
									<div className="editor-panel-templates-card-exos">
										{names.reduce((acc, n, i) => i === 0 ? [<span key={i}>{n}</span>] : [...acc, " · ", <span key={i}>{n}</span>], [])}
										{exos.length > names.length && <span> +{exos.length - names.length}</span>}
									</div>
								)}
								<div className="editor-panel-templates-card-cta">
									{libraryMode ? "Partir de ce template →" : "Charger comme séance →"}
								</div>
							</button>
						);
					})
				)}
			</div>
		</div>
	);
}
